// Components/LetterPicker.jsx - Updated with skip functionality

import React, { useState, useEffect, useRef } from "react";
import { PaperMascot } from "./Mascot";

// How long the picked letter celebrates before the round starts.
const REVEAL_MS = 1100;

const LetterPicker = ({
  isOpen,
  onLetterSelected,
  playedLetters = [],
  currentRound,
  playerName,
  timeLimit = 10,
  isHost = false,
  onHostSkip = null,
  currentPickerName = "",
}) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showHostSkip, setShowHostSkip] = useState(false);
  const [revealLetter, setRevealLetter] = useState(null);
  const revealTimer = useRef(null);

  useEffect(() => () => clearTimeout(revealTimer.current), []);

  useEffect(() => {
    if (isOpen) return;
    clearTimeout(revealTimer.current);
    revealTimer.current = null;
    setRevealLetter(null);
  }, [isOpen]);

  const allLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const availableLetters = allLetters.filter(
    (letter) => !playedLetters.includes(letter)
  );

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return;

    setTimeLeft(timeLimit);
    setSelectedLetter(null);
    setShowHostSkip(false);

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Auto-select random letter if time runs out
          if (!selectedLetter && !revealTimer.current) {
            const randomLetter =
              availableLetters[
                Math.floor(Math.random() * availableLetters.length)
              ];
            onLetterSelected(randomLetter, true); // true = auto-selected
          }
          return 0;
        }
        
        // DELETE_FLAG: Show host skip button after 20 seconds for host
        if (isHost && prev === timeLimit - 20 && timeLimit > 20) {
          setShowHostSkip(true);
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, timeLimit, selectedLetter, availableLetters, onLetterSelected, isHost]);

  const handleLetterClick = (letter) => {
    if (revealLetter) return;
    setRevealLetter(letter);
    revealTimer.current = setTimeout(() => {
      setSelectedLetter(letter);
      onLetterSelected(letter, false); // false = manually selected
    }, REVEAL_MS);
  };

  const handleHostSkip = () => {
    if (isHost && onHostSkip) {
      const randomLetter =
        availableLetters[Math.floor(Math.random() * availableLetters.length)];
      onHostSkip(randomLetter);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-red-800 mb-2">
              Your Turn to Pick!
            </h2>
            <p className="text-gray-600">
              <span className="font-semibold">{playerName}</span>, choose the
              letter for Round {currentRound}
            </p>
          </div>

          {/* Available Letters Grid */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4 text-center text-base">
              Available Letters:
            </h3>

            <div className="grid grid-cols-6 md:grid-cols-8 gap-2">
              {availableLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  disabled={selectedLetter !== null || revealLetter !== null}
                  className={`aspect-square flex items-center justify-center text-xl font-bold rounded-lg border-2 transition-all duration-200 ${
                    (revealLetter || selectedLetter) === letter
                      ? "bg-red-600 text-white border-red-600 transform scale-110"
                      : selectedLetter !== null || revealLetter !== null
                      ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : "bg-white text-red-600 border-red-300 hover:bg-red-100 hover:border-red-500 hover:transform hover:scale-105"
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Selection Confirmation */}
          {revealLetter && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center flex flex-col items-center">
              <PaperMascot letter={revealLetter} mood="celebrate" size={120} className="nm-drop" label={`Letter ${revealLetter}`} />
              <div className="text-red-800 font-semibold mb-1">
                You picked{" "}
                <span className="text-2xl font-bold">{revealLetter}</span>
              </div>
              <p className="text-red-600 text-sm">
                Round {currentRound} will start with this letter!
              </p>
            </div>
          )}

          {/* Host Skip Button */}
          {isHost && showHostSkip && !selectedLetter && !revealLetter && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-yellow-800 text-sm mb-3">
                <span className="font-semibold">{currentPickerName}</span> is taking too long to pick...
              </p>
              <button
                onClick={handleHostSkip}
                className="bg-yellow-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-yellow-700 transition duration-300"
              >
                Skip & Auto-Pick Letter
              </button>
            </div>
          )}

          {/* <div className="mt-4 flex flex-col items-center">
            <p className="text-sm text-gray-500">
              Computer will auto-select if no letter is chosen in {timeLeft}{" "}
              seconds
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default LetterPicker;